import { CLOUD_GAME_CHANNEL_KEY } from './config.mjs'
import { SESSION_STATE, assertTransition, isActiveAttempt } from './session-state.mjs'

const DEFAULT_SCHEDULER = {
    setTimeout (handler, delay) {
        return setTimeout(handler, delay)
    },
    clearTimeout (timer) {
        clearTimeout(timer)
    }
}

const CONNECT_FAIL_STATE = 3
const SENSITIVE_DIAGNOSTIC_KEYS = new Set([
    'auth',
    'clog',
    'gsmToken',
    'packedData',
    'sdkMsg',
    'token',
    'verify'
])

function safeObjectKeys (data) {
    return Object.keys(data).filter(key => !SENSITIVE_DIAGNOSTIC_KEYS.has(key)).sort()
}

function dispatchDiagnostics (data) {
    return {
        sessionId: data.sessionId || '',
        traceId: data.traceId || '',
        nodeId: data.nodeId || '',
        gsId: data.gsId || '',
        recordId: data.recordId || '',
        provider: data.provider === undefined ? '' : data.provider
    }
}

function sanitizeConnectExtraData (extraData) {
    if (!extraData || typeof extraData !== 'object') return {}
    const safeKeys = ['isReconnect', 'isAutoConnect', 'code', 'message', 'rtcType']
    return safeKeys.reduce((result, key) => {
        if (Object.prototype.hasOwnProperty.call(extraData, key)) result[key] = extraData[key]
        return result
    }, {})
}

function summarizeStreamExtraData (data) {
    if (!data || typeof data !== 'object') return { valueType: typeof data }
    const summary = { keys: safeObjectKeys(data) }
    if (typeof data.code === 'number' || typeof data.code === 'string') summary.code = data.code
    if (data.msg && typeof data.msg === 'object') {
        summary.messageKeys = safeObjectKeys(data.msg)
        if (Object.prototype.hasOwnProperty.call(data.msg, 'playerRole')) {
            summary.playerRole = data.msg.playerRole
        }
    }
    return summary
}

function summarizePipeData (data) {
    if (!data || typeof data !== 'object') return { valueType: typeof data }
    return { keys: safeObjectKeys(data) }
}

function normalizeDispatch (data) {
    if (!data || typeof data.sdkMsg !== 'string' || !data.sdkMsg) {
        throw new Error('调度响应缺少有效的 sdkMsg')
    }

    const mode = data.identiType === 'queuing' ? 'queue' : 'direct'
    const queueToken = data.token || data.queueToken || data.identi || ''
    if (mode === 'queue' && !queueToken) {
        throw new Error('排队响应缺少有效的 token')
    }

    return {
        mode,
        launchPayload: data.sdkMsg,
        sessionId: data.sessionId || '',
        diagnostics: dispatchDiagnostics(data),
        queueToken,
        seatNo: Number(data.seatNo || 0),
        queuingSize: Number(data.queuingSize || 0)
    }
}

function isQueueComplete (data) {
    if (!data) return false
    if (typeof data.sdkMsg === 'string' && data.sdkMsg) return true
    if (data.sessionId) return true
    if (data.completed === true || data.queuing === false) return true
    return Boolean(data.identiType && data.identiType !== 'queuing')
}

export function createSessionController ({
    api,
    adapter,
    config,
    scheduler = DEFAULT_SCHEDULER,
    onState = () => {},
    onQueue = () => {},
    onFirstFrame = () => {},
    onStatistics = () => {},
    onRecoverableError = () => {},
    onQueueCancelError = () => {},
    onError = () => {},
    onStopped = () => {},
    onDiagnostic = () => {},
    logger = console
}) {
    let state = SESSION_STATE.IDLE
    let attemptId = 0
    let activeAttemptId = 0
    let launchPayload = null
    let sessionId = ''
    let queueToken = ''
    let queueTimer = null
    let startTimer = null
    let startPromise = null
    let stopPromise = null
    let clientToken = ''
    let channelAdId = ''
    let pipeConnected = false
    let startStartedAt = 0
    let diagnosticContext = {}
    let firstStatisticsLogged = false

    const snapshot = () => ({
        state,
        attemptId,
        launchPayload,
        sessionId,
        queueToken
    })

    const emitDiagnostic = (event, detail = {}, level = 'info') => {
        const now = Date.now()
        try {
            onDiagnostic({
                time: new Date(now).toISOString(),
                level,
                event,
                state,
                attemptId: activeAttemptId || attemptId,
                elapsedMs: startStartedAt ? now - startStartedAt : 0,
                ...diagnosticContext,
                ...detail
            })
        } catch (error) {
            if (logger && typeof logger.warn === 'function') {
                logger.warn('[CloudGame Diagnostic] logging failed', error.message)
            }
        }
    }

    const transition = nextState => {
        const previousState = state
        state = assertTransition(state, nextState)
        emitDiagnostic('state-change', { from: previousState, to: nextState })
        onState(state, snapshot())
    }

    const callbackIsCurrent = event => {
        return activeAttemptId !== 0 &&
            (event.attemptId === undefined || isActiveAttempt(event.attemptId, activeAttemptId))
    }

    const createCancelledError = () => {
        const error = new Error('云游戏启动已取消')
        error.cancelled = true
        error.handled = true
        return error
    }

    const ensureActiveAttempt = currentAttemptId => {
        if (!isActiveAttempt(currentAttemptId, activeAttemptId)) {
            throw createCancelledError()
        }
    }

    const clearQueueTimer = () => {
        if (!queueTimer) return
        scheduler.clearTimeout(queueTimer)
        queueTimer = null
    }

    const clearStartTimer = () => {
        if (!startTimer) return
        scheduler.clearTimeout(startTimer)
        startTimer = null
    }

    const clearSensitiveState = () => {
        launchPayload = null
        queueToken = ''
        clientToken = ''
        channelAdId = ''
        pipeConnected = false
        startStartedAt = 0
        diagnosticContext = {}
        firstStatisticsLogged = false
    }

    const scheduleStartTimeout = currentAttemptId => {
        const timeoutMs = Number(config.startTimeout || 0)
        if (timeoutMs <= 0) return
        startTimer = scheduler.setTimeout(async () => {
            startTimer = null
            if (!isActiveAttempt(currentAttemptId, activeAttemptId) || state !== SESSION_STATE.STARTING) return
            emitDiagnostic('start-timeout', { timeoutMs }, 'error')
            onError({
                phase: 'start-timeout',
                message: '云游戏连接超时，请重试',
                timeoutMs,
                ...diagnosticContext
            })
            await stop('start-timeout')
        }, timeoutMs)
    }

    const startStreaming = async currentAttemptId => {
        ensureActiveAttempt(currentAttemptId)
        startStartedAt = Date.now()
        firstStatisticsLogged = false
        transition(SESSION_STATE.STARTING)
        const payload = launchPayload
        queueToken = ''
        launchPayload = null
        pipeConnected = false
        adapter.beginAttempt(currentAttemptId)
        emitDiagnostic('sdk-start-call')
        scheduleStartTimeout(currentAttemptId)
        await adapter.startGame(payload)
        ensureActiveAttempt(currentAttemptId)
        emitDiagnostic('sdk-start-resolved')
    }

    const scheduleQueueQuery = (currentAttemptId, currentQueueToken) => {
        if (state !== SESSION_STATE.QUEUING || queueTimer) return
        queueTimer = scheduler.setTimeout(async () => {
            queueTimer = null
            if (!isActiveAttempt(currentAttemptId, activeAttemptId) ||
                state !== SESSION_STATE.QUEUING || queueToken !== currentQueueToken) return
            try {
                emitDiagnostic('queue-query-start')
                const data = await api.post('/v1/cloud/queue-query', { token: currentQueueToken }, null, {
                    retry: 0,
                    timeout: 5000
                })
                ensureActiveAttempt(currentAttemptId)
                if (state !== SESSION_STATE.QUEUING || queueToken !== currentQueueToken) return
                onQueue({
                    seatNo: Number((data && data.seatNo) || 0),
                    queuingSize: Number((data && data.queuingSize) || 0)
                })
                emitDiagnostic('queue-query-success', {
                    seatNo: Number((data && data.seatNo) || 0),
                    queuingSize: Number((data && data.queuingSize) || 0),
                    complete: isQueueComplete(data)
                })
                if (isQueueComplete(data)) {
                    await startStreaming(currentAttemptId)
                    return
                }
            } catch (error) {
                if (!isActiveAttempt(currentAttemptId, activeAttemptId)) return
                if (state === SESSION_STATE.QUEUING) {
                    emitDiagnostic('queue-query-fail', { message: error.message }, 'warn')
                    onRecoverableError(error)
                }
            }
            scheduleQueueQuery(currentAttemptId, currentQueueToken)
        }, config.queuePollInterval)
    }

    const stop = (reason = 'unknown', options = {}) => {
        if (stopPromise) return stopPromise
        if (state === SESSION_STATE.IDLE) return Promise.resolve()

        const previousState = state
        const tokenToCancel = queueToken
        const stoppedSessionId = sessionId
        emitDiagnostic('stop-request', { reason, previousState })
        activeAttemptId = 0
        if (adapter.endAttempt) adapter.endAttempt()
        transition(SESSION_STATE.STOPPING)
        clearQueueTimer()
        clearStartTimer()

        const operation = (async () => {
            try {
                if (previousState === SESSION_STATE.QUEUING && tokenToCancel) {
                    try {
                        await api.post('/v1/cloud/queue-cancel', { token: tokenToCancel }, null, {
                            retry: 0,
                            timeout: 5000
                        })
                    } catch (error) {
                        onQueueCancelError(error)
                    }
                } else if ([SESSION_STATE.STARTING, SESSION_STATE.PLAYING].includes(previousState)) {
                    if (options.skipSdkExit) {
                        if (adapter.waitForPendingStart) await adapter.waitForPendingStart()
                    } else {
                        await adapter.exitGame()
                    }
                }
            } finally {
                sessionId = ''
                transition(SESSION_STATE.IDLE)
                startPromise = null
                emitDiagnostic('stop-complete', { reason, stoppedSessionId })
                onStopped(reason, { sessionId: stoppedSessionId })
                clearSensitiveState()
            }
        })()
        stopPromise = operation
        const clearStopPromise = () => {
            if (stopPromise === operation) stopPromise = null
        }
        operation.then(clearStopPromise, clearStopPromise)
        return operation
    }

    adapter.on('first-frame', event => {
        if (!callbackIsCurrent(event) || state !== SESSION_STATE.STARTING) return
        clearStartTimer()
        emitDiagnostic('first-video-frame')
        transition(SESSION_STATE.PLAYING)
        onFirstFrame(snapshot())
    })
    adapter.on('statistics', event => {
        if (!callbackIsCurrent(event) || state !== SESSION_STATE.PLAYING) return
        if (!firstStatisticsLogged) {
            firstStatisticsLogged = true
            emitDiagnostic('first-stream-statistics', {
                netWorkDelayUdp: Number((event.data && event.data.netWorkDelayUdp) || 0),
                fps: Number((event.data && event.data.fps) || 0),
                bitrate: Number((event.data && event.data.bitrate) || 0),
                packetLossRate: Number((event.data && event.data.packetLossRate) || 0)
            })
        }
        onStatistics(event.data)
    })
    adapter.on('bitrate-range', event => {
        if (!callbackIsCurrent(event) ||
            ![SESSION_STATE.STARTING, SESSION_STATE.PLAYING].includes(state)) return
        emitDiagnostic('bitrate-range', {
            status: event.status,
            lowerLimit: event.lowerLimit,
            upperLimit: event.upperLimit,
            message: event.message
        }, event.status === 'set' ? 'info' : 'warn')
    })
    adapter.on('connect-state', event => {
        if (!callbackIsCurrent(event) ||
            ![SESSION_STATE.STARTING, SESSION_STATE.PLAYING].includes(state)) return
        const extraData = sanitizeConnectExtraData(event.extraData)
        emitDiagnostic('connect-state', { type: event.type, extraData })
        if (Number(event.type) !== CONNECT_FAIL_STATE) return
        emitDiagnostic('connect-fail', { type: event.type, extraData }, 'error')
        onError({
            phase: 'connect',
            code: event.type,
            message: '云游戏连接失败，请重试',
            extraData,
            ...diagnosticContext
        })
        stop('connect-fail')
    })
    adapter.on('decode-fail', event => {
        if (!callbackIsCurrent(event) ||
            ![SESSION_STATE.STARTING, SESSION_STATE.PLAYING].includes(state)) return
        emitDiagnostic('decode-fail', { code: event.code, message: event.message }, 'error')
        onError({
            phase: 'decode',
            code: event.code,
            message: event.message || '云游戏画面解码失败，请重试',
            ...diagnosticContext
        })
        stop('decode-fail')
    })
    adapter.on('stream-extra-data', event => {
        if (!callbackIsCurrent(event) ||
            ![SESSION_STATE.STARTING, SESSION_STATE.PLAYING].includes(state)) return
        emitDiagnostic('stream-extra-data', {
            type: event.type,
            data: summarizeStreamExtraData(event.data)
        })
    })
    adapter.on('channel-state', event => {
        if (!callbackIsCurrent(event) ||
            ![SESSION_STATE.STARTING, SESSION_STATE.PLAYING].includes(state)) return
        emitDiagnostic('channel-state', { channelState: event.state })
        if (event.state !== 1) {
            pipeConnected = false
            return
        }
        if (pipeConnected) return
        pipeConnected = true
    })
    adapter.on('login-request', event => {
        if (!callbackIsCurrent(event) || !pipeConnected ||
            ![SESSION_STATE.STARTING, SESSION_STATE.PLAYING].includes(state) || !clientToken) return
        logger.info('[CloudGame Pipe] received login', {
            received: {
                key: event.key || 'login',
                data: summarizePipeData(event.data)
            },
            response: {
                key: 'logindata',
                data: {
                    tokenPresent: Boolean(clientToken),
                    tokenLength: clientToken.length,
                    adid: channelAdId
                }
            }
        })
        emitDiagnostic('pipe-login-response', {
            tokenPresent: Boolean(clientToken),
            tokenLength: clientToken.length,
            adid: channelAdId
        })
        adapter.sendLoginData(clientToken, channelAdId)
    })
    adapter.on('start-fail', event => {
        if (!callbackIsCurrent(event) || state !== SESSION_STATE.STARTING) return
        emitDiagnostic('start-fail', { code: event.code, message: event.message }, 'error')
        onError({ phase: 'start', code: event.code, message: event.message })
        stop('start-fail')
    })
    adapter.on('remote-exit', event => {
        if (!callbackIsCurrent(event) ||
            ![SESSION_STATE.STARTING, SESSION_STATE.PLAYING].includes(state)) return
        emitDiagnostic('remote-exit', { code: event.code, message: event.message }, 'error')
        onError({ phase: 'remote-exit', code: event.code, message: event.message })
        stop('remote-exit', { skipSdkExit: true })
    })

    return {
        start ({ precheck, clientToken: nextClientToken = '', channelAdId: nextChannelAdId = '' }) {
            if (startPromise) return startPromise

            attemptId += 1
            const currentAttemptId = attemptId
            activeAttemptId = currentAttemptId
            clientToken = nextClientToken
            channelAdId = nextChannelAdId
            diagnosticContext = {}
            startStartedAt = 0
            emitDiagnostic('session-start', {
                browserOnline: typeof navigator === 'undefined' ? undefined : navigator.onLine
            })
            transition(SESSION_STATE.PRECHECK)

            startPromise = (async () => {
                try {
                    const precheckResult = await precheck()
                    ensureActiveAttempt(currentAttemptId)
                    emitDiagnostic('precheck-success')
                    if (precheckResult) {
                        if (Object.prototype.hasOwnProperty.call(precheckResult, 'clientToken')) {
                            clientToken = precheckResult.clientToken
                        }
                        if (Object.prototype.hasOwnProperty.call(precheckResult, 'channelAdId')) {
                            channelAdId = precheckResult.channelAdId
                        }
                    }
                    emitDiagnostic('sdk-initialize-start')
                    await adapter.initialize()
                    ensureActiveAttempt(currentAttemptId)
                    emitDiagnostic('sdk-initialize-success')
                    transition(SESSION_STATE.MEASURING)
                    const cmdInfo = await api.get('/v1/cloud/cmd-info', { key: CLOUD_GAME_CHANNEL_KEY })
                    ensureActiveAttempt(currentAttemptId)
                    if (!cmdInfo || !cmdInfo.cmd) {
                        throw new Error('未获取到官方渠道启动命令')
                    }
                    emitDiagnostic('cmd-info-success')
                    const node = await adapter.measureBestNode()
                    ensureActiveAttempt(currentAttemptId)
                    emitDiagnostic('node-measure-success', { measuredNodeId: node })
                    const codecType = await adapter.getMediaCodecType()
                    ensureActiveAttempt(currentAttemptId)
                    emitDiagnostic('codec-detected', { codecType })
                    const bizData = adapter.getBizData()
                    transition(SESSION_STATE.DISPATCHING)
                    emitDiagnostic('dispatch-start', {
                        measuredNodeId: node,
                        codecType: String(codecType),
                        primaryX: config.primaryX,
                        primaryY: config.primaryY
                    })
                    const response = await api.post('/v1/cloud/dispatch', {
                        primaryX: config.primaryX,
                        primaryY: config.primaryY,
                        bizData,
                        node,
                        codecType: String(codecType),
                        cmdLine: cmdInfo.cmd
                    }, null, {
                        retry: 0,
                        timeout: 8000
                    })
                    ensureActiveAttempt(currentAttemptId)
                    const dispatch = normalizeDispatch(response)
                    launchPayload = dispatch.launchPayload
                    sessionId = dispatch.sessionId
                    diagnosticContext = dispatch.diagnostics
                    emitDiagnostic('dispatch-success', { mode: dispatch.mode })
                    if (dispatch.mode === 'queue') {
                        queueToken = dispatch.queueToken
                        transition(SESSION_STATE.QUEUING)
                        onQueue({ seatNo: dispatch.seatNo, queuingSize: dispatch.queuingSize })
                        scheduleQueueQuery(currentAttemptId, dispatch.queueToken)
                    } else {
                        await startStreaming(currentAttemptId)
                    }
                    return { mode: dispatch.mode, sessionId }
                } catch (error) {
                    if (error.cancelled || !isActiveAttempt(currentAttemptId, activeAttemptId)) {
                        throw createCancelledError()
                    }
                    if (![SESSION_STATE.STOPPING, SESSION_STATE.IDLE].includes(state)) {
                        const failedState = state
                        emitDiagnostic('start-flow-error', {
                            failedState,
                            code: error.code,
                            message: error.message
                        }, 'error')
                        transition(SESSION_STATE.ERROR)
                        if (!error.handled) {
                            onError({
                                phase: failedState.toLowerCase(),
                                message: error.message,
                                code: error.code,
                                error
                            })
                        }
                        await stop('start-error')
                    }
                    throw error
                }
            })()
            return startPromise
        },
        stop,
        getSnapshot: snapshot
    }
}
